import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { History, ChevronRight } from "lucide-react";
import { useAuth } from "@/App";
import { getRecentlyViewed } from "@/lib/recentlyViewed";
import { saleActive, discountedStartingPrice } from "@/lib/pricing";

const priceLabel = (p) => {
  if (p.has_variants && p.starting_price > 0) {
    const d = discountedStartingPrice(p);
    return `From $${(d ?? p.starting_price).toFixed(2)}`;
  }
  const v = saleActive(p) ? p.sale_price : p.price;
  return v > 0 ? `$${v.toFixed(2)}` : "";
};

export const RecentlyViewed = ({ excludeIds = [], contained = false, className = "" }) => {
  const { user } = useAuth();
  const excludeKey = excludeIds.join(",");
  const items = useMemo(() => getRecentlyViewed(excludeIds).slice(0, 8), [excludeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user || items.length === 0) return null;

  const inner = (
    <div className={className} data-testid="recently-viewed">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-white flex items-center gap-2.5">
          <History className="w-5 h-5 text-[#00AEEF]" />
          Recently Viewed
        </h2>
        <Link to="/shop" className="text-[#00AEEF] text-sm hover:underline flex items-center gap-1" data-testid="rv-shop-link">
          Browse all <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 snap-x" style={{ scrollbarWidth: "thin" }}>
        {items.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
            className="w-40 flex-shrink-0 snap-start"
          >
            <Link
              to={`/product/${p.id}`}
              className="group block bg-[#0F1115] border border-white/5 rounded-xl overflow-hidden hover:border-[#00AEEF]/40 transition-all"
              data-testid={`rv-card-${p.id}`}
            >
              <div className="h-32 bg-[#0a0b0e] flex items-center justify-center overflow-hidden">
                {p.image && (
                  <img src={p.image} alt={p.name} className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-300" loading="lazy" width="160" height="128" />
                )}
              </div>
              <div className="p-3">
                <p className="text-white text-sm font-medium truncate group-hover:text-[#00AEEF] transition-colors">{p.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[#00AEEF] text-sm font-bold">{priceLabel(p)}</span>
                  {saleActive(p) && !p.has_variants && (
                    <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 px-1.5 py-0.5 rounded">SALE</span>
                  )}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );

  if (!contained) return inner;
  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">{inner}</div>
    </section>
  );
};
